// Pure helpers for the Gantt view.
// Date construction uses explicit (year, month, day, 12, 0, 0, 0) to dodge timezone drift,
// matching the pattern used in Schedule.js.

export const MS_PER_DAY = 86400000;

export const toLocalDay = (dateLike) => {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
};

export const toLocalISODate = (date) => {
  if (!date) return null;
  const d = toLocalDay(date);
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const addDays = (date, days) => {
  const d = toLocalDay(date);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days, 12, 0, 0, 0);
};

export const daysBetween = (start, end) => {
  const a = toLocalDay(start);
  const b = toLocalDay(end);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
};

// 1-indexed column number for CSS Grid placement.
export const dateToColumn = (date, viewportStart) => {
  const offset = daysBetween(viewportStart, date);
  return offset + 1;
};

// Compute the date range covering all projects + their milestones,
// with padding on each side so bars never sit flush against the edges.
export const getDateRange = (projects, paddingDays = 14) => {
  let earliest = null;
  let latest = null;

  const consider = (dateLike) => {
    const d = toLocalDay(dateLike);
    if (!d) return;
    if (!earliest || d < earliest) earliest = d;
    if (!latest || d > latest) latest = d;
  };

  (projects || []).forEach((p) => {
    consider(p.start_date);
    consider(p.end_date);
    (p.milestones || []).forEach((m) => {
      consider(m.start_date);
      consider(m.due_date);
    });
  });

  // Always include "today" in the range so the today-line is visible.
  const today = toLocalDay(new Date());
  consider(today);

  if (!earliest || !latest) {
    // No dates anywhere — show a 60-day window around today.
    return {
      start: addDays(today, -30),
      end: addDays(today, 30),
    };
  }

  return {
    start: addDays(earliest, -paddingDays),
    end: addDays(latest, paddingDays),
  };
};

export const snapToDay = (pixels, columnWidth) => {
  if (!columnWidth) return 0;
  return Math.round(pixels / columnWidth);
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Build header rows for the timeline. Returns { primary: [{label, span}], secondary: [{label, span}] }
// where span is the number of day-columns the cell covers.
export const buildHeaderCells = (start, totalDays, scale) => {
  const primary = [];
  const secondary = [];

  if (totalDays <= 0) return { primary, secondary };

  if (scale === 'day') {
    // Primary: months. Secondary: day numbers.
    let cursor = toLocalDay(start);
    let monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
    let monthSpan = 0;

    for (let i = 0; i < totalDays; i++) {
      const d = addDays(start, i);
      const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
      if (label !== monthLabel) {
        primary.push({ label: monthLabel, span: monthSpan });
        monthLabel = label;
        monthSpan = 0;
      }
      monthSpan += 1;
      secondary.push({ label: String(d.getDate()), span: 1, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    primary.push({ label: monthLabel, span: monthSpan });
  } else if (scale === 'week') {
    // Primary: months. Secondary: week-start dates (every 7 days).
    let cursor = toLocalDay(start);
    let monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
    let monthSpan = 0;

    for (let i = 0; i < totalDays; i++) {
      const d = addDays(start, i);
      const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
      if (label !== monthLabel) {
        primary.push({ label: monthLabel, span: monthSpan });
        monthLabel = label;
        monthSpan = 0;
      }
      monthSpan += 1;

      if (i % 7 === 0) {
        const weekSpan = Math.min(7, totalDays - i);
        secondary.push({ label: `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`, span: weekSpan });
      }
    }
    primary.push({ label: monthLabel, span: monthSpan });
  } else {
    // month scale. Primary: years. Secondary: months.
    let cursor = toLocalDay(start);
    let yearLabel = String(cursor.getFullYear());
    let yearSpan = 0;
    let monthCursor = cursor.getMonth();
    let monthYear = cursor.getFullYear();
    let monthSpan = 0;

    for (let i = 0; i < totalDays; i++) {
      const d = addDays(start, i);
      if (String(d.getFullYear()) !== yearLabel) {
        primary.push({ label: yearLabel, span: yearSpan });
        yearLabel = String(d.getFullYear());
        yearSpan = 0;
      }
      yearSpan += 1;

      if (d.getMonth() !== monthCursor || d.getFullYear() !== monthYear) {
        secondary.push({ label: `${MONTH_NAMES[monthCursor]}`, span: monthSpan });
        monthCursor = d.getMonth();
        monthYear = d.getFullYear();
        monthSpan = 0;
      }
      monthSpan += 1;
    }
    primary.push({ label: yearLabel, span: yearSpan });
    secondary.push({ label: `${MONTH_NAMES[monthCursor]}`, span: monthSpan });
  }

  return { primary, secondary };
};

export const COLUMN_WIDTHS = {
  day: 42,
  week: 26,
  month: 12,
};

export const formatShortDate = (dateLike) => {
  const d = toLocalDay(dateLike);
  if (!d) return '';
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
};

export const isWeekend = (dateLike) => {
  const d = toLocalDay(dateLike);
  if (!d) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
};
